// FoMehrfachChip.tsx — Kontaktverantwortlicher FO im Pillen-Band der
// Kontaktakte (`.verantrow`), seit 28.09.2026 mit MEHREREN FO:
//
//   [ FO: (MaF)(CLL) ▾ ] [ BO: Team Leben ▾ ] [ Interessent ▾ ]
//
// Je FO ein Kürzel-Kreis — dieselben Kreise wie in der Kontakttabelle
// (`shared/foBo.tsx`, `FoKreis`), damit dieselbe Person hier und dort denselben
// Farbton trägt. Das Menü ist eine Mehrfachauswahl mit Kästchen, Suchfeld und
// „Fertig"-Knopf; gespeichert wird beim Schließen (Fertig oder Klick daneben)
// in EINEM Aufruf (PUT /contact/:id/ownership mit `foUserIds`). Escape
// verwirft die Änderung.
//
// Eigene Komponente statt eines Umbaus von `VerantwortlichChip`: Der BO bleibt
// eine Einfachauswahl, und die Kontaktakte tauscht nur die FO-Pille aus.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";

import { FoKreis, hashColor, type FoBoUser } from "../shared/foBo";
import { filterVerantOptionen } from "./VerantwortlichChip";
import { foUmschalten } from "../../utils/foUserIds";
import { aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

const MENU_W = 280;

export interface FoMehrfachOption {
  id: string;
  name: string;
  kuerzel?: string | null;
}

export interface FoMehrfachChipProps {
  /** Aktuelle FO-Ids, primärer zuerst. */
  gewaehlt: string[];
  /** Wählbare FO (Frontoffice-Nutzer); unbekannte gewählte Ids zeigt die Pille trotzdem. */
  optionen: FoMehrfachOption[];
  /**
   * Namens-/Kürzel-Quelle für gewählte FO, die nicht (mehr) in `optionen`
   * stehen (z. B. alle User des Mandanten). Sie bleiben so benannt und abwählbar.
   */
  namen?: FoMehrfachOption[];
  /** Nur mit `canReassignOwnership` — sonst statische Pille ohne Menü. */
  editable?: boolean;
  /** Neue Liste (primärer zuerst). Wird nur bei einer echten Änderung aufgerufen. */
  onChange?: (ids: string[]) => void;
}

/** Gleiche Liste (Reihenfolge zählt: der erste ist der primäre FO)? */
export function gleicheFoListe(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

export function FoMehrfachChip({ gewaehlt, optionen, namen, editable, onChange }: FoMehrfachChipProps) {
  const [menuAt, setMenuAt] = React.useState<{ x: number; y: number } | null>(null);
  const [query, setQuery] = React.useState("");
  const [entwurf, setEntwurf] = React.useState<string[]>(gewaehlt);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const sucheRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (menuAt) sucheRef.current?.focus();
  }, [menuAt]);

  const bezeichnung = "Kontaktverantwortlicher FO";
  const nachId = React.useMemo(
    () => new Map([...(namen ?? []), ...optionen].map((o) => [o.id, o])),
    [namen, optionen],
  );
  const waehlbar = React.useMemo(() => new Set(optionen.map((o) => o.id)), [optionen]);
  const alsFoUser = (id: string): FoBoUser => {
    const o = nachId.get(id);
    return { userId: id, kuerzel: o?.kuerzel ?? null, displayName: o?.name || "Unbekannter Nutzer" };
  };
  const gewaehlteNamen = gewaehlt.map((id) => nachId.get(id)?.name || "Unbekannter Nutzer");
  const titel = `${bezeichnung}: ${gewaehlteNamen.length ? gewaehlteNamen.join(", ") : "Kein FO"}`;
  // Kapsel: bei genau einem FO in dessen Farbe (wie bisher), bei mehreren
  // neutral — die Kreise tragen dann die Farben.
  const farbe = gewaehlt.length === 1 ? hashColor(gewaehlt[0]) : null;
  const stil = farbe ? { background: farbe.bg, color: farbe.fg } : undefined;
  const klasse = "verantpill" + (farbe ? "" : " leer");

  const inhalt = (
    <>
      <span className="k">FO:</span>
      {gewaehlt.length === 0 ? (
        <span className="n">Kein FO</span>
      ) : (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
          {gewaehlt.map((id) => (
            <FoKreis key={id} foUser={alsFoUser(id)} size={20} />
          ))}
        </span>
      )}
      {editable && <span className="statuspill-caret">▾</span>}
    </>
  );

  if (!editable) {
    return (
      <span className={klasse + " static"} style={stil} title={titel}>
        {inhalt}
      </span>
    );
  }

  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setQuery("");
    setEntwurf(gewaehlt);
    setMenuAt({ x: Math.max(8, Math.min(r.left, aktivesFenster().innerWidth - MENU_W - 8)), y: r.bottom + 6 });
  };

  const schliessen = (speichern: boolean) => {
    setMenuAt(null);
    setQuery("");
    // Ein Kontakt verliert seinen FO hier nicht ganz (wie bisher: FO lässt sich
    // nur ersetzen, nicht entfernen) — eine leere Auswahl ändert nichts.
    if (speichern && entwurf.length > 0 && !gleicheFoListe(entwurf, gewaehlt)) onChange?.(entwurf);
  };

  // Gewählte, die der Abteilungsfilter nicht (mehr) liefert, bleiben abwählbar.
  const alleOptionen: FoMehrfachOption[] = [
    ...optionen,
    ...entwurf
      .filter((id) => !waehlbar.has(id))
      .map((id) => ({ id, name: nachId.get(id)?.name || "Unbekannter Nutzer", kuerzel: nachId.get(id)?.kuerzel })),
  ];
  const treffer = filterVerantOptionen(alleOptionen, query);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={klasse + " verantpill-btn"}
        style={stil}
        onClick={openMenu}
        title={`${titel} — ändern`}
        aria-haspopup="listbox"
      >
        {inhalt}
      </button>

      {menuAt &&
        createPortal(
          <div className="kvp-rd-scrim statuspill-scrim" onClick={() => schliessen(true)}>
            <div
              className="kvp-rd statuspill-menu verantchip-menu"
              style={{ left: menuAt.x, top: menuAt.y, width: MENU_W }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.stopPropagation();
                  schliessen(false);
                }
              }}
            >
              <div className="verantchip-kopf">
                <span className="verantchip-titel">{bezeichnung} (mehrere möglich)</span>
                <span className="verantchip-hinweis">
                  Wird mit „Fertig" gespeichert · der erste ist der primäre FO
                </span>
              </div>
              <input
                ref={sucheRef}
                className="verantchip-suche"
                value={query}
                placeholder="Suchen …"
                autoComplete="off"
                aria-label={`${bezeichnung} durchsuchen`}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="verantchip-liste" role="listbox" aria-multiselectable aria-label={bezeichnung}>
                {treffer.map((o) => {
                  const an = entwurf.includes(o.id);
                  const primaer = entwurf[0] === o.id && entwurf.length > 1;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      role="option"
                      aria-selected={an}
                      className={"statuspill-item verantchip-item" + (an ? " on" : "")}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                      onClick={() => setEntwurf((e) => foUmschalten(e, o.id))}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 15,
                          height: 15,
                          flexShrink: 0,
                          display: "inline-grid",
                          placeItems: "center",
                          borderRadius: 4,
                          border: "1px solid var(--line-2)",
                          background: an ? "var(--aub-50, #f6eef4)" : "var(--field, #fff)",
                          color: "var(--aub, #7d2169)",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {an ? "✓" : ""}
                      </span>
                      <span style={{ flexGrow: 1, minWidth: 0 }}>
                        {o.name}
                        {o.kuerzel && <span className="verantchip-kuerzel">{o.kuerzel}</span>}
                      </span>
                      {primaer && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-3)" }}>primär</span>
                      )}
                    </button>
                  );
                })}
                {treffer.length === 0 && <div className="verantchip-leer">Keine Treffer.</div>}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 4 }}>
                <button
                  type="button"
                  style={{
                    border: 0,
                    borderRadius: 8,
                    padding: "6px 14px",
                    background: "var(--aub, #7d2169)",
                    color: "#fff",
                    font: "700 13px/1.2 var(--ui)",
                    cursor: "pointer",
                  }}
                  onClick={() => schliessen(true)}
                >
                  Fertig
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default FoMehrfachChip;
